import {Box, CssBaseline, Stack, Typography, useColorScheme} from "@mui/material";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Experimental_CssVarsProvider as CssVarsProvider, StyledEngineProvider} from "@mui/material/styles";
import StripeProvider from "../../../hooks/use-stripe";
import useQuery from "../../../hooks/use-query";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import axios from "axios";
import theme from "../../../theme";
import NewStripeCreditCardForm from "../../../components/app-specific/new-credit-card-form/stripe";
import Button from "../../../components/base/button/button";
import {useHistory} from "react-router-dom";
import {routes} from "../../../../core/models/static/routes/app-routes";
import {CreditCardProviders} from "../../../../core/constants/enums";
import usePartnerColorTheme from "../../../hooks/use-partner-color-theme";

class Logger {
    constructor(notifyListeners) {
        this._logs = [];
        this._notifyListeners = notifyListeners;
    }

    log(...messages) {
        this._logs.push(...messages);
        this._notifyListeners?.();
    }

    get logs() {
        return this._logs;
    }
}


const PhoneStripeCardSetupView = () => {
    const [query] = useQuery();
    const {replace: historyReplace} = useHistory();
    const [responses, setResponses] = useState([]);
    const isMounted = useIsMounted();

    const authToken = useMemo(() => query.authToken, [query.authToken]);
    const colorMode = useMemo(() => query.colorMode, [query.colorMode]);
    const customerId = useMemo(() => query.customerId, [query.customerId]);
    const successUrl = useMemo(() => query.successUrl, [query.successUrl]);
    const errorUrl = useMemo(() => query.errorUrl, [query.errorUrl]);
    const [, setRefresher] = useState(false);
    const notifyListeners = useCallback(() => setRefresher(prevState => !prevState), []);

    const logger = useMemo(() => new Logger(notifyListeners), []);

    const missingCriticalParams = useMemo(() => {
        return !authToken || !customerId || !successUrl;
    }, [authToken, customerId, successUrl])

    /**
     * Redirects to the error URL if any of the critical parameters are missing.
     */
    useLayoutEffect(() => {
        if (missingCriticalParams && errorUrl) {
            window.location = errorUrl;
        } else if (missingCriticalParams) {
            historyReplace(routes.error.notFound + "?hideBackButton=true");
        }
    }, [missingCriticalParams, historyReplace, errorUrl]);

    const {partnerColorTheme} = usePartnerColorTheme({
        autoFetch: !!authToken,
        autoFetchArgs: {
            customAuthToken: authToken,
            disregardCache: true,
        }
    });
    const generatedTheme = useMemo(() => theme(partnerColorTheme), [partnerColorTheme]);

    /**
     * Generates the headers for the API calls with the authorization token.
     * @type {function(): {Authorization: string[] | string}}
     */
    const getApiHeaders = useCallback(() => ({
        Authorization: authToken,
    }), [authToken]);


    /**
     * Fetches the list of credit card providers from the server.
     * @type {(function(*): Promise<void>)}
     */
    const getCreditCardProviders = useCallback(async (cancelToken) => {
        const response = await Api.getPaymentProviders(getApiHeaders, cancelToken, true, logger);
        if (!isMounted())
            return;
        setResponses(prev => [...prev, response]);
        const hasStripe = response?.data?.some(provider => provider.name === CreditCardProviders.stripe);
        if (!hasStripe && errorUrl) {
            historyReplace(routes.error.accessDenied + "?hideBackButton=true");
        }
    }, [isMounted, errorUrl, historyReplace, logger, getApiHeaders])

    /**
     * Listens for the credit card providers fetched event and checks whether stripe is enabled.
     */
    useEffect(() => {
        if (!authToken)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getCreditCardProviders(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [authToken, getCreditCardProviders]);


    /**
     * Handles the success callback of the credit card form.
     * @type {(function(*): void)}
     */
    const onSuccess = useCallback(() => {
        window.location = successUrl;
    }, [successUrl])

    return (
        <>
            <div data-mui-color-scheme={colorMode ?? 'light'}>
                <CssVarsProvider
                    defaultMode={colorMode ?? 'system'}
                    theme={generatedTheme}
                    modeStorageKey={'phone-stripe-card-setup-color-scheme'}
                    colorSchemeStorageKey={'phone-stripe-card-setup-color-scheme'}
                >
                    <CssBaseline enableColorScheme/>
                    <StyledEngineProvider injectFirst>
                        <StripeProvider authToken={authToken} bypassAuthCheck>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '100dvh',
                                    maxWidth: '100vw',
                                    padding: 2,
                                    '@supports (-webkit-touch-callout: none)': {
                                        minHeight: '-webkit-fill-available',
                                    },
                                }}>
                                <PhoneStripeCardSetup
                                    colorMode={colorMode}
                                    customerId={customerId}
                                    onSuccess={onSuccess}
                                    logger={logger}
                                    getApiHeaders={getApiHeaders}
                                />
                                {/*<Stack sx={{width: '100%'}} spacing={2}>*/}
                                {/*    <Box>*/}
                                {/*        <Typography variant={'h6'} fontWeight={600} gutterBottom>*/}
                                {/*            Theme:*/}
                                {/*        </Typography>*/}
                                {/*        <ThemeRenderer/>*/}
                                {/*    </Box>*/}
                                {/*    <Box>*/}
                                {/*        <Typography variant={'h6'} fontWeight={600} gutterBottom>*/}
                                {/*            Responses:*/}
                                {/*        </Typography>*/}
                                {/*        {responses.map((response, index) => (*/}
                                {/*            <Box key={index} sx={{marginBottom: 2}}>*/}
                                {/*                <Typography variant={'body2'} fontWeight={500} gutterBottom>*/}
                                {/*                    Response {index + 1}:*/}
                                {/*                </Typography>*/}
                                {/*                <JsonRenderer data={response}/>*/}
                                {/*            </Box>*/}
                                {/*        ))}*/}
                                {/*    </Box>*/}
                                {/*    <Box>*/}
                                {/*        <Typography variant={'h6'} fontWeight={600} gutterBottom>*/}
                                {/*            Logger:*/}
                                {/*        </Typography>*/}
                                {/*        <JsonRenderer data={logger.logs}/>*/}
                                {/*    </Box>*/}
                                {/*</Stack>*/}
                            </Box>
                        </StripeProvider>
                    </StyledEngineProvider>
                </CssVarsProvider>
            </div>
        </>
    );
}

const PhoneStripeCardSetup = ({colorMode, customerId, onSuccess, logger, getApiHeaders}) => {
    const {setMode} = useColorScheme();
    const [submitting, setSubmitting] = useState(false);
    const formRef = useRef();

    /**
     * Sets the color mode based on the query parameter if it is different from the current mode.
     */
    useEffect(() => {
        if (colorMode) setMode(colorMode);
    }, [colorMode, setMode]);

    /**
     * Submits the new credit card form with the option of saving its data.
     * @type {function(): Promise<void>}
     */
    const addCreditCard = useCallback(async () => {
        await formRef.current?.submit(true);
    }, []);

    return (
        <Box
            data-mui-color-scheme={colorMode ?? 'light'}
            sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}
        >
            <Stack flexDirection={'column'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant={'h5'} fontWeight={600} gutterBottom textAlign={'start'} width={'100%'}>
                    Credit Card Information
                </Typography>

                <Typography variant={'body2'} fontWeight={500} gutterBottom>
                    Please enter the customer credit card information to be saved for future transactions.
                </Typography>
                <NewStripeCreditCardForm
                    bypassAuthCheck
                    getApiHeaders={getApiHeaders}
                    onSuccess={onSuccess}
                    customerId={customerId}
                    setSubmitting={setSubmitting}
                    className={'w-100'}
                    ref={formRef}
                    logger={logger}
                />
                <Button disabled={submitting} variant={'contained'} color={'primary'} onClick={addCreditCard} size={'large'} sx={{marginBlockStart: 3, minWidth: 200}}>
                    {submitting ? "Adding..." : 'Add'}
                </Button>
            </Stack>

        </Box>
    );
}

export default PhoneStripeCardSetupView;